import React, { useState } from 'react';
import useSoftwareRefund from '../../../hooks/use-software-refund';
import Button from '../../button';
import useUpdateSoftwareRefund from '../../../hooks/use-update-software-refund';
import useRefundSoftwareOrder from '../../../hooks/use-refund-software-order';
import useCreateSoftwareRefund from '../../../hooks/use-create-software-refund';

interface Props {
  info: any;
  isAdmin?: boolean;
}

const SoftwareInfoItem: React.FC<Props> = ({ info, isAdmin = false }) => {
  const [isProcessing, setProcessing] = useState(false);

  const softwareOrder = info.order;
  const softwareDownload = info.download;
  const softwareRefund = useSoftwareRefund(softwareDownload.swdlId);

  const updateSoftwareRefund = useUpdateSoftwareRefund(() => {
    // window.location.href = '/admin/orders/software';
    setProcessing(false);
  });

  const refundOrder = useRefundSoftwareOrder(
    (data) => {
      const refund = data.refundOrder;

      updateSoftwareRefund({
        swdlId: softwareDownload.swdlId,
        refundNumber: refund.orderNumber,
        status: 'CREDITED',
      });
    },
    (error) => {
      console.error(error);
    }
  );

  const createSoftwareRefund = useCreateSoftwareRefund(() => {
    refundOrder(softwareOrder.orderNumber);
  });

  const daysDiff = (date1, date2) => {
    const diff = Math.abs(date1.getTime() - date2.getTime());
    return diff / (1000 * 60 * 60 * 24);
  };

  const isRefundAllowed = () => {
    return daysDiff(new Date(), new Date(softwareOrder.orderDate)) <= 14;
  };

  const refundStatus = () => {
    if (!softwareRefund) return null;

    switch (softwareRefund.status) {
      case 'CREDITED': {
        return 'Credited';
      }

      case 'REJECTED': {
        return 'Rejected';
      }

      default: {
        return 'Requested';
      }
    }
  };

  const onRefund = () => {
    setProcessing(true);

    createSoftwareRefund({
      swdlId: softwareDownload.swdlId,
      userId: softwareOrder.userId,
      reasonForRefund: '',
      status: 'APPROVED',
      helpRequestId: null,
      downloadReason: '',
      downloadResult: '',
    });
  };

  if (!softwareOrder) return null;

  return (
    <React.Fragment>
      <div className="order-tile" key={softwareOrder.softwareOrderId}>
        <span>{softwareOrder.orderNumber}</span>
        <span>{new Date(softwareOrder.orderDate).toLocaleDateString()}</span>
        <span>{softwareOrder.vin}</span>
        <span>{softwareOrder.modelYear}</span>
        <span>{softwareOrder.vehicleType}</span>
        <span>{softwareDownload.download}</span>
        <span>${softwareOrder.price.toFixed(2)}</span>
        <span>${softwareDownload.credit?.toFixed(2)}</span>
        {isAdmin ? (
          <React.Fragment>
            {softwareDownload && (
              <React.Fragment>
                {!softwareRefund ? (
                  <span>
                    <Button
                      text="Refund"
                      margin={false}
                      disabled={isProcessing}
                      onClick={() => onRefund()}
                    />
                  </span>
                ) : (
                  <span>
                    <Button
                      text="Refund Info"
                      margin={false}
                      disabled={isProcessing}
                      onClick={() => {
                        window.location.href = `/admin/orders/software/refunds/${softwareRefund.swdlId}`;
                      }}
                    />
                  </span>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {!softwareRefund && isRefundAllowed() ? (
              <span>
                <Button
                  text="Request Refund"
                  margin={false}
                  onClick={() => {
                    window.location.href = `/account/software/${softwareOrder.softwareOrderId}/refund`;
                  }}
                />
              </span>
            ) : (
              <span>{refundStatus()}</span>
            )}
          </React.Fragment>
        )}
      </div>
      {/* language=CSS */}
      <style jsx>{`
        a {
          color: #333;
          font-family: 'Volvo Novum', sans-serif;
          margin-bottom: 1rem;
          text-decoration: none;
          transition: color 200ms linear;
        }

        a:hover {
          color: #707070;
        }

        .order-tile {
          display: flex;
          align-items: center;
        }

        .order-tile span:first-child {
          flex: 1 0 10%;
        }

        .order-tile span:nth-child(3) {
          flex: 1 0 20%;
        }

        .order-tile span:last-child {
          flex: 1 0 15%;
        }

        .order-tile span {
          flex: 1 0 10%;
          font-family: 'Volvo Novum', sans-serif;
          font-size: 14px;
          font-weight: 400;
        }
      `}</style>
    </React.Fragment>
  );
};

export default SoftwareInfoItem;
